import React from 'react';
import AnchorLink from 'react-anchor-link-smooth-scroll';

import Drawer from '@mui/material/Drawer';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import ViewListIcon from '@mui/icons-material/ViewList';
import HistoryIcon from '@mui/icons-material/History';

import { getAPI } from '../util/APIUtils';

import { downloadData } from 'aws-amplify/storage';

import { ViewerState, Caption, Metadata, History, Revisiondata, Catalog, UploadFile, Field } from '../states/viewerState';
import { green } from '@mui/material/colors';
import { OpenDialogRequest, UploadFileDialog } from './UploadFileDialog';
import { AppState } from '../states/appState';
import { ViewerActions } from '../containers/viewerContainer';
import { FileUploadAPIParam } from '../actions/viewerAction';

import { styled } from '@mui/material/styles'

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: any;
  value: any;
}

const ViewDrawer = (props) => (
  <Drawer
    sx={{
      width: 360,
      flexShrink: 0,
      backgroundColor: (theme) => theme.palette.primary[50],
      // paper自体はスクロールさせない（スクロールバーの上端が固定ヘッダーに潜り込むため）
      [`& .MuiDrawer-paper`]: { width: 360, boxSizing: 'border-box', overflow: 'hidden' },
    }}
    variant="permanent"
  >{props.children}</Drawer>
);

// クリックできる要素はハンドポインターに統一する
const pointerCursor = { cursor: 'pointer' };

// 折り畳み／展開アイコン。サイズが目次の行の高さを決めるため、行間に合わせて絞る
const expandIconSx = { ...pointerCursor, fontSize: '32px' };

const EditingTypeTextSpan = styled('span')(({ theme }) => ({
  fontWeight: 'bold',
  marginRight: theme.spacing(1),
  fontSize: '0.97em',
}));

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div>{children}</div>
      )}
    </div>
  );
};

const a11yProps = (index: any) => {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
};

const NestedListItem = (props) => (
  <ListItem sx={{
    paddingLeft: 2 * props.level + 2,
    // 行間は文字サイズの3〜4割程度に詰める
    paddingTop: '3px',
    paddingBottom: '3px',
    backgroundColor: (theme) => theme.palette.grey[100],
    [`& .MuiListItemText-root`]: { marginTop: 0, marginBottom: 0 },
  }}>{props.children}</ListItem>
);

// 初期表示で配下を展開したままにするノード種別
// 編6 / 章7 / 節8 / 款9 / 目10
// これ以外は折りたたむ。条を開くと項、項を開くと号、と自分で開いた要素の
// 配下だけが見える状態になる。
const EXPANDED_CAPTION_TYPES = ['6', '7', '8', '9', '10'];

/**
 * 見出しツリーの開閉状態の初期値を作成。
 */
const createInitialOpens = (captionList: Caption[], countHasChild: number) => {
  const opens = Array(countHasChild).fill(true);
  const collapse = (list: Caption[]) => {
    list.forEach((item) => {
      if (item.hasChildIndex >= 0 && !EXPANDED_CAPTION_TYPES.includes(item.type)) {
        opens[item.hasChildIndex] = false;
      }
      if (item.childCaptionList) collapse(item.childCaptionList);
    });
  };
  collapse(captionList);
  return opens;
};

type ViewerProps = {} & ViewerState & AppState & ViewerActions;

export const Viewer: React.FC<ViewerProps> = (props) => {
  const [value, setValue] = React.useState(0);

  // アップロードファイルダイアログ
  const [anchorElDialog, setAnchorElDialog] = React.useState(false);
  const openDialog = Boolean(anchorElDialog);
  const handleCloseDialog = () => setAnchorElDialog(false);
  const [openDialogRequest, setOpenDialogRequest] = React.useState<OpenDialogRequest>({ actId: '', nodeId: '', uploadFile: [] });
  const handleUploadFileDialog = (actId: string, nodeId: string, uploadFile: UploadFile[] = []) => {
    setOpenDialogRequest({ actId: actId, nodeId: nodeId, uploadFile: uploadFile })
    setAnchorElDialog(true);
  };

  const completeUploadFileDialog = (nodeId: string, uploadFile: UploadFile[]) => {
    handleCloseDialog();
    const apiParam: FileUploadAPIParam = {
      type: props.type,
      actId: props.actId,
      revision: props.revision,
      editingId: props.editingId,
      stepId: props.stepId,
      miekesi: props.miekesi,
      highlight: props.highlight,
      nodeId: props.nodeId,
      accountId: props.accountid,
      idid: props.idid,
      uploadNodeId: nodeId,
      uploadFile: uploadFile,
      mode: props.mode
    }
    props.setLoading(true);
    props.comitUploadFileAsync(apiParam);
  };

  // ダウンロード
  const handleDownload = async (actId: string, nodeId: string, fileName: string) => {
    if (process.env.REACT_APP_LGWAN && process.env.REACT_APP_LGWAN === 'LG-WAN') {
      downloadFormLGWAN(actId, nodeId, fileName);
    } else {
      downloadForm(actId, nodeId, fileName);
    }
  };

  const downloadFormLGWAN = async (actId: string, nodeId: string, fileName: string) => {
    const path = `${actId}/Uploaded/${nodeId}/${fileName}`;

    const apiurl = `/tempfile?path=private/${props.idid}/${path}`;
    const json = await getAPI(apiurl);
    const realdownloadurl = (process.env.REACT_APP_LGWAN_S3_REVERSE_ENDPOINT && process.env.REACT_APP_LGWAN_S3_VPC_ENDPOINT) ? json.downloadUrl.replace(process.env.REACT_APP_LGWAN_S3_VPC_ENDPOINT, process.env.REACT_APP_LGWAN_S3_REVERSE_ENDPOINT) : json.downloadUrl;

    const file = await fetch(realdownloadurl);
    const blob = await file.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', path);
    document.body.appendChild(link);
    link.click();
  };

  const downloadForm = async (actId: string, nodeId: string, fileName: string) => {
    const path = `${actId}/Uploaded/${nodeId}/${fileName}`;
    //const file = await Storage.get(path, { level: 'private' });
    const downloadTask = await downloadData({
      path: `private/${props.idid}/${path}`,
    });
    const { body } = await downloadTask.result;
    const blob = new Blob([await body.blob()]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    //const filename = path.split('/').pop();
    link.setAttribute('download', path);
    document.body.appendChild(link);
    link.click();
  };

  // Nodeツリー
  // 見出しはAPI応答後に届くため、届いた時点で開閉状態を作り直す。
  // useEffectで作り直すと全開きの状態が一度描画され、開→閉のアニメーションが見えてしまうため、
  // 描画前（レンダリング中）に作り直す。
  const [openState, setOpenState] = React.useState(() => ({
    caption: props.caption,
    opens: createInitialOpens(props.caption, props.countHasChild),
  }));
  if (openState.caption !== props.caption) {
    setOpenState({
      caption: props.caption,
      opens: createInitialOpens(props.caption, props.countHasChild),
    });
  }
  const opens = openState.opens;
  const setOpens = (newOpens: boolean[]) => setOpenState({ caption: props.caption, opens: newOpens });

  // 画面描画後に対象ノードIDにジャンプするようReact.useEffectを利用
  React.useEffect(() => {
    // 見出しクリックで対象ノードにジャンプ
    (document.getElementById('a' + props.nodeId))?.click();
  }, [props.nodeId]);

  const handleClick = (index: number) => {
    const copy = opens.slice();
    copy[index] = !copy[index];
    setOpens(copy);
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  const [catalogOpens, setCatalogOpens] = React.useState(Array(props.metadata.countHasCatalogChild).fill(true));
  const handleCatalogClick = (index: number) => {
    const copy = catalogOpens.slice();
    copy[index] = !copy[index];
    setCatalogOpens(copy);
  };

  const [historyOpens, setHistoryOpens] = React.useState(Array(props.metadata.countHasHistoryChild).fill(false));
  const handleHistoryClick = (index: number) => {
    const copy = historyOpens.slice();
    copy[index] = !copy[index];
    setHistoryOpens(copy);
  };

  const expandColor = (item: Caption) => {
    return item.editing || item.otherEditing ?
      { color: '#FF0000' } : { color: green[300] };
  };

  const openViewer = (revison: string) => {
    const query = `?type=${'revision'}&actId=${props.actId}&revision=${revison}&miekesi=true&mode=history`;
    const url = process.env.REACT_APP_VIEWER_URL ? process.env.REACT_APP_VIEWER_URL.replace('{hostname}', window.location.hostname) : process.env.REACT_APP_VIEWER_URL;
    window.open(url + query, `viewer_${props.actId}_${revison}`);
  };

  /**
   * 編集状態、リンク可否を判定し見出し字句を作成。
   * @param item 
   * @param parent 
   */
  const createCaptionText = (item: Caption, parent?: Caption) => {
    const t2Editing = item.editing && item.editing.editing === 'all' && item.editing.editing_top ?
      (<EditingTypeTextSpan>[全部改正]</EditingTypeTextSpan>) :
      item.editing && item.editing.editing === 'add' && item.editing.editing_top ?
        (<EditingTypeTextSpan>[追加]</EditingTypeTextSpan>) :
        item.editing && item.editing.editing === 'add' && item.editing.editing_type === 'division' ?
          (<EditingTypeTextSpan>[章等名追加]</EditingTypeTextSpan>) :
          item.editing && item.editing.editing === 'erase' ?
            (<EditingTypeTextSpan>[削る]</EditingTypeTextSpan>) :
            item.editing && item.editing.editing === 'delete' ?
              (<EditingTypeTextSpan>[削除]</EditingTypeTextSpan>) :
              item.editing && item.editing.editing_words ?
                (<EditingTypeTextSpan>[一部編集]</EditingTypeTextSpan>) :
                item.editing && item.editing.editing_table ?
                  (<EditingTypeTextSpan>[表編集]</EditingTypeTextSpan>) :
                  item.otherEditing ?
                    (<EditingTypeTextSpan>{`[${item.otherEditing}]`}</EditingTypeTextSpan>) : undefined;

    if (item.abbrevFlg || item.notviewFlg || parent?.abbrevFlg || parent?.notviewFlg) {
      return (
        <ListItemText disableTypography
          primary={
            <Typography variant="body1" sx={{ fontSize: '0.97em', }}>{t2Editing}{item.value}</Typography>}
        />
      )
    } else {
      return t2Editing ? (
        <ListItemText disableTypography
          primary={
            <AnchorLink id={'a' + item.id} href={`#${item.id}`} offset="64">
              <Typography variant="body1" sx={{
                fontSize: '0.97em',
                backgroundColor: (theme) => theme.palette.secondary[50],
              }}>{t2Editing}{item.value}</Typography>
            </AnchorLink>
          }
        />
      ) : (
        <ListItemText disableTypography
          primary={
            <AnchorLink id={'a' + item.id} href={`#${item.id}`} offset="64">
              <Typography variant="body1" sx={{ fontSize: '0.97em', }}>{t2Editing}{item.value}</Typography>
            </AnchorLink>
          }
        />
      );
    }
  };

  /**
   * 見出しツリー作成
   * @param item 
   * @param parent 
   */
  const createCapitonItems = (item: Caption, parent?: Caption) => {
    if (item.childCaptionList) {
      const childlen = item.childCaptionList.map(item2 => createCapitonItems(item2, item));
      return (
        <div key={'d' + item.id}>
          <NestedListItem key={item.id} level={item.level}>
            {opens[item.hasChildIndex] ?
              <ExpandLess sx={expandIconSx} style={expandColor(item)} onClick={() => handleClick(item.hasChildIndex)} />
              :
              <ExpandMore sx={expandIconSx} style={expandColor(item)} onClick={() => handleClick(item.hasChildIndex)} />}
            {createCaptionText(item, parent)}
          </NestedListItem>
          <Collapse in={opens[item.hasChildIndex]} timeout="auto">
            <List component="div" disablePadding>
              {childlen}
            </List>
          </Collapse>
        </div>
      );
    }
    return (
      <NestedListItem key={'d' + item.id} level={item.level}>
        {createCaptionText(item, parent)}
      </NestedListItem>
    );
  };

  /**
   * 体系表示
   * @param item 
   */
  const createCatalogItems = (item: Catalog) => {
    if (item.child) {
      const childlen = createCatalogItems(item.child!);
      return (
        <div key={'d' + item.name}>
          <NestedListItem key={item.name} level={item.level}>
            {catalogOpens[item.hasChildIndex] ?
              <ExpandLess sx={expandIconSx} style={{ color: green[300] }} onClick={() => handleCatalogClick(item.hasChildIndex)} />
              :
              <ExpandMore sx={expandIconSx} style={{ color: green[300] }} onClick={() => handleCatalogClick(item.hasChildIndex)} />}
            <ListItemText disableTypography
              primary={
                <Typography variant="body1" sx={{ fontSize: '0.97em', }}>{item.name}</Typography>}
            />
          </NestedListItem>
          <Collapse in={catalogOpens[item.hasChildIndex]} timeout="auto">
            <List component="div" disablePadding>
              {childlen}
            </List>
          </Collapse>
        </div>
      );
    }
    return (
      <NestedListItem key={'d' + item.name} level={item.level}>
        <ListItemText disableTypography
          primary={
            <Typography variant="body1" sx={{ fontSize: '0.97em', }}>{item.name}</Typography>}
        />
      </NestedListItem>
    );
  }

  /**
   * 分野表示
   * @param item 
   */
  const createFieldItems = (item: Field[]) => {
    return (
      <>
        {item.map((row: Field) => (
          <NestedListItem key={`field${row.id}`} level={0}>
            <ListItemText disableTypography
              primary={
                <Typography variant="body1" sx={{ fontSize: '0.97em', }}>{row.name}</Typography>
              }
            />
          </NestedListItem>
        ))}
      </>
    )
  };

  /**
   * 沿革表示
   * @param item 
   */
  const createHistoryItems = (item: History) => {
    const createRevisonText = (item2: Revisiondata) => {
      if (!item2.link) {
        return (
          <ListItemText disableTypography
            primary={
              <Typography variant="body1" sx={{ fontSize: '0.97em', }}>{item2.date} {item2.type}</Typography>}
          />
        )
      } else {
        return (
          <ListItemText sx={{
            fontSize: '0.97em',
            color: '#0000FF',
          }} disableTypography
            primary={
              <Typography onClick={() => openViewer(item2.revision)} variant="body1" sx={{ fontSize: '0.97em', ...pointerCursor }}>{item2.date} {item2.type}</Typography>
            }
          />
        )
      }
    };

    const createUploadFileItem = () => {
      if (item.uploadFile) {
        return (
          <>
            {item.uploadFile.map(row => (
              <NestedListItem key={row.type + item.nodeId} level={2}>
                <ListItemText sx={{
                  fontSize: '0.97em',
                  color: '#0000FF',
                }} disableTypography
                  primary={
                    <Typography onClick={() => handleDownload(props.actId, item.nodeId, row.fileName)} variant="body1" sx={{ fontSize: '0.97em', ...pointerCursor }}>{row.alias}</Typography>
                  }
                />
              </NestedListItem>
            ))}
          </>
        );
      } else {
        return (<></>);
      }
    };

    const createUploadFileDialogLink = () => {
      return props.role === 'Viewer' ? '' : (<NestedListItem key={'uploafFile' + item.nodeId} level={1}>
        <ListItemText sx={{
          fontSize: '0.97em',
          color: '#0000FF',
        }} disableTypography
          primary={
            <Typography onClick={() => handleUploadFileDialog(props.actId, item.nodeId, item.uploadFile)} variant="body1" sx={{ fontSize: '0.97em', ...pointerCursor }}>{'添付資料'}</Typography>
          }
        />
      </NestedListItem>);
    };

    return (
      <div key={'d' + item.editActNum}>
        <NestedListItem key={item.editActNum} level={item.level}>
          {historyOpens[item.hasChildIndex] ?
            <ExpandLess sx={expandIconSx} style={{ color: green[300] }} onClick={() => handleHistoryClick(item.hasChildIndex)} />
            :
            <ExpandMore sx={expandIconSx} style={{ color: green[300] }} onClick={() => handleHistoryClick(item.hasChildIndex)} />}
          <ListItemText disableTypography
            primary={
              <Typography variant="body1" dir="ltr">{item.editActNum}</Typography>
            }
          />
        </NestedListItem>
        <Collapse in={historyOpens[item.hasChildIndex]} timeout="auto">
          <List component="div" disablePadding>
            {item.revisiondata.map(row => (
              <NestedListItem key={'d' + row.revision} level={row.level}>
                {createRevisonText(row)}
              </NestedListItem>
            ))}
            {createUploadFileDialogLink()}
            {createUploadFileItem()}
          </List>
        </Collapse>
      </div>
    );
  };

  const createMetadataItems = (metadata: Metadata) => {
    return (
      <Box sx={{ backgroundColor: (theme) => theme.palette.grey[100], }}>
        <Typography align='left' variant="subtitle1" sx={{
          paddingTop: (theme) => theme.spacing(2),
          paddingLeft: (theme) => theme.spacing(2),
        }}>{metadata.title}</Typography>
        <Typography align='left' variant="subtitle1" sx={{
          paddingTop: (theme) => theme.spacing(2),
          paddingLeft: (theme) => theme.spacing(2),
        }}>{metadata.actNum}</Typography>
        <Typography align='left' variant="subtitle1" sx={{
          paddingTop: (theme) => theme.spacing(2),
          paddingLeft: (theme) => theme.spacing(2),
          borderBottom: '1px solid',
        }}>体系</Typography>
        {createCatalogItems(metadata.catalog)}
        <Typography align='left' variant="subtitle1" sx={{
          paddingTop: (theme) => theme.spacing(2),
          paddingLeft: (theme) => theme.spacing(2),
          borderBottom: '1px solid',
        }}>分野</Typography>
        {createFieldItems(metadata.field)}
        <Typography align='left' variant="subtitle1" sx={{
          paddingTop: (theme) => theme.spacing(2),
          paddingLeft: (theme) => theme.spacing(2),
          borderBottom: '1px solid',
        }}>沿革</Typography>
        {props.metadata.history.map(row => (
          createHistoryItems(row)
        ))}
      </Box>
    );
  };

  const createTabs = () => {
    if (props.type === 'revision') {
      return (
        <>
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="full width tabs example"
          >
            <Tab label={
              <Typography variant="caption" gutterBottom>
                <ViewListIcon fontSize='small' color='primary' sx={{ marginBottom: 1, marginRight: 1, verticalAlign: 'middle' }} />
                見出し
              </Typography>
            } {...a11yProps(0)} />
            <Tab label={
              <Typography variant="caption" gutterBottom>
                <HistoryIcon fontSize='small' color='primary' sx={{ marginBottom: 1, marginRight: 1, verticalAlign: 'middle' }} />
                体系・分野・沿革
              </Typography>
            } {...a11yProps(1)} />
          </Tabs>
          <TabPanel value={value} index={0} dir={'rtl'}>
            <List>
              {props.caption.map(row => (
                createCapitonItems(row)
              ))}
            </List>
          </TabPanel>
          <TabPanel value={value} index={1} dir={'rtl'}>
            {createMetadataItems(props.metadata)}
          </TabPanel>
        </>
      );
    } else {
      return (
        <>
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="full width tabs example"
          >
            <Tab label="見出し" {...a11yProps(0)} />
          </Tabs>
          <TabPanel value={value} index={0} dir={'rtl'}>
            <List>
              {props.caption.map(row => (
                createCapitonItems(row)
              ))}
            </List>
          </TabPanel>
        </>
      );
    }
  };
  const createActState = () => {
    const stateText = String(props.actState) === '1' ? '廃止'
      : String(props.actState) === '2' ? '失効'
        : String(props.actState) === '3' ? '全部改正廃止'
          : '';
    if (stateText !== '') {
      return (<>{stateText}されています</>)
    } else {
      return (<></>)
    }
  };

  /**
   * ウィンドウタブタイトル差し替え
   */
  const replaceTitle = () => {
    document.title = props.title ? `${props.title} | LegalCRUD` : `LegalCRUD`;
    return '';
  };

  return (
    <Box sx={{ display: 'flex', }}>
      {replaceTitle()}
      <ViewDrawer>
        <Box sx={(theme) => ({ ...theme.mixins.toolbar, flexShrink: 0 })} />
        {/* ヘッダーより下だけをスクロール領域にする */}
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          {createTabs()}
        </Box>
      </ViewDrawer>
      <Box sx={{
        flexGrow: 1,
        padding: (theme) => theme.spacing(3),
      }}>
        {createActState()}
        <div id='contents' dangerouslySetInnerHTML={{ __html: props.contents }}></div>
      </Box>
      <UploadFileDialog
        openDialogRequest={openDialogRequest}
        open={openDialog}
        idid={props.idid}
        handleClose={handleCloseDialog}
        completeUploadFileDialog={completeUploadFileDialog}
      />
    </Box >
  );
}
